import { TripEstimateView } from "@/components/booking/create/TripEstimateView";
import { CreateRequestLayout } from "@/components/customer/CreateRequestLayout";
import { useAuth } from "@/contexts/auth-context";
import { getApiErrorMessage } from "@/lib/api";
import { buildBookingPayload } from "@/lib/build-booking-payload";
import pricingService from "@/lib/services/pricing";
import { showToast } from "@/lib/toast";
import { isLocationOnlyService } from "@/lib/validation/booking";
import { useCreateRequestStore } from "@/stores/use-create-request-store";
import { useQuery } from "@tanstack/react-query";
import { router } from "expo-router";
import React, { useEffect, useState } from "react";
import { InspectionAddonCard, isInspectionEligible } from "./InspectionAddonCard";

export const Estimate = ({ route = true }: { route?: boolean }) => {
  const { user } = useAuth();
  const { formData, setCurrentStep } = useCreateRequestStore();
  const [creating, setCreating] = useState(false);
  const canEstimate = !!formData.from_latitude && !!formData.from_longitude;

  const serviceType = formData.service_type;
  const locationOnly = isLocationOnlyService(serviceType);

  const {
    data: estimate = null,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ["guest-price-estimate", formData],
    enabled: !user && canEstimate,
    retry: 0,
    queryFn: async () => {
      try {
        return await pricingService.estimatePrice(
          buildBookingPayload(formData),
        );
      } catch (error: unknown) {
        showToast.error(
          getApiErrorMessage(error, "Failed to get price estimate"),
        );
        throw error;
      }
    },
  });

  useEffect(() => {
    if (user) {
      router.replace("/customer/create-request/summary");
    }
  }, [user]);

  const handleGetEstimate = () => {
    if (!canEstimate) {
      showToast.error("Pickup location is missing");
      return;
    }
    refetch();
  };

  const handleCreateBooking = async () => {
    if (!estimate) {
      showToast.warning("Please wait for the estimate");
      return;
    }
    try {
      setCreating(true);
      const { checkout_url } = await pricingService.createCheckout(
        buildBookingPayload(formData, { paymentMethod: "card" }),
      );

      router.push(checkout_url as any);
    } catch (error: unknown) {
      showToast.error(getApiErrorMessage(error, "Failed to start checkout"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <CreateRequestLayout
      step={5}
      title={locationOnly ? "Cost" : "Trip Estimate"}
      subtitle={
        locationOnly
          ? "Based on the options selected"
          : "Based on your route and vehicle"
      }
      onNext={handleCreateBooking}
      onBack={() => (route ? router.back() : setCurrentStep(4))}
      nextLabel={
        !user
          ? "Pay & Complete Booking"
          : formData.scheduled_at &&
              new Date(formData.scheduled_at).setHours(0, 0, 0, 0) >
                new Date().setHours(0, 0, 0, 0)
            ? "Book Service"
            : "Request Service Now"
      }
      isNextLoading={creating}
      isNextDisabled={loading || !estimate}
    >
      <TripEstimateView
        loading={loading}
        estimate={estimate}
        locationOnly={locationOnly}
        fromAddress={formData.from_address}
        toAddress={formData.to_address}
        onRetry={handleGetEstimate}
        addon={
          isInspectionEligible(serviceType) ? (
            <InspectionAddonCard
              serviceType={serviceType}
              inspectionRequired={formData.inspection_required}
              inspectionTier={formData.inspection_tier}
              onChange={(values) =>
                useCreateRequestStore.getState().setFormData(values)
              }
            />
          ) : null
        }
      />
    </CreateRequestLayout>
  );
};
